import { get, off, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import { getCreativeTemplateFolderIndexUpdates } from "./creativeTemplateFolders";
import { getUserID } from "./getters";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import types from "../types";
import { CREATIVE_JOB_TYPES, LaunchError } from "../utils/errors";
import { getKeyDatabase, startUploadFileAws } from "../services/public";
import {
  ALERTS_ASYNC_STATUS,
  CREATIVE_TEMPLATE_ANALYSIS_STATUS,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
  CREATIVE_TEMPLATE_CONTINUOUS_CAROUSEL_ENABLED,
  CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
  CREATIVE_TEMPLATE_LAYOUT_KIND,
  CREATIVE_TEMPLATE_MAX_NODES,
  CREATIVE_TEMPLATE_PRODUCE_INTENT,
  CREATIVE_TEMPLATE_PRODUCE_STATUS,
  CREATIVE_TEMPLATE_SCHEMA_VERSION,
  CREATIVE_TEMPLATE_STATUS_APPROVED,
  CREATIVE_TEMPLATE_STATUS_DRAFT,
} from "../utils/constants";
import { parseCreativeTemplateDoc } from "../features/creativeTemplates/schemas/creativeTemplateDocSchema";
import {
  countNodes,
  sanitizeArtboardsForPersist,
  sanitizeNodesForPersist,
} from "../features/creativeTemplates/utils/layerTree";
import {
  findReferenceArtboardID,
  getCardArtboardFields,
  getReferenceAssetKey,
  hydrateArtboards,
  isolateCarouselSlideGuides,
  referenceAssetIDIsShared,
  listBackdropAssets,
  resolveActiveArtboardID,
  resolveArtboardSizeFromDimensions,
} from "../features/creativeTemplates/utils/artboards";
import {
  countAllLayoutNodes,
  findSlideIndex,
  flushActiveCell,
  getFormatArtboards,
  getSlideWorkingArtboards,
  isCarouselLayout,
  listProcessingSlideIDs,
  mergeAnalyzingSlides,
  resolveLayoutKind,
  seedCarouselSlides,
} from "../features/creativeTemplates/utils/slides";
import { stripToSlideArtboards } from "../features/creativeTemplates/utils/continuousStripArtboards";
import { getContinuousSlideCount } from "../features/creativeTemplates/utils/continuousStrip";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import { getImageDimensionsFromFile } from "../features/creativeTemplates/utils/getImageDimensionsFromFile";
import { isLayerizeImageTooSmall } from "../features/creativeTemplates/utils/layerizeImageConstraints";
import { addNotification, updateNotification } from "./alerts";
import { createCreativeJob, createCreativeJobAndWaitForResponse } from "./jobs";
import { promoteCreativeJobNotification } from "./creativeJobs";
import { getUniqueID } from "../utils/numbers";
import { CREATIVE_JOB_STATUS } from "../utils/creativeJobStatus";
import {
  getCreativeJobAlertMessageKey,
  mapJobResultToGenerationData,
} from "../utils/creativeJobsNotifications";
import {
  startSaveCreativeTemplateProduce,
  startUpdateCreativeTemplateProduceStatus,
  startPrepareProduceRetry,
} from "./creativeTemplatesProduce";
import {
  getProduceVariationsPerRow,
  PRODUCE_MAX_ROWS,
  serializeProduceColumns,
} from "../features/creativeTemplates/produce/produceRows";

export const CREATIVE_TEMPLATE_DELETE_MODE = {
  DELETE: "delete",
  DISCARD_DRAFT: "discardDraft",
};

export const hideCreativeTemplatePendingDelete = ({ templateID }) => ({
  type: types.CREATIVE_TEMPLATES_HIDE_PENDING_DELETE,
  payload: { templateID },
});

export const clearCreativeTemplatePendingDelete = ({ templateID }) => ({
  type: types.CREATIVE_TEMPLATES_CLEAR_PENDING_DELETE,
  payload: { templateID },
});

export const pruneCreativeTemplatePendingDeletes = ({ existingIDs = [] }) => ({
  type: types.CREATIVE_TEMPLATES_PRUNE_PENDING_DELETES,
  payload: { existingIDs },
});

const resolveTemplateNameAndNodeCount = ({ card, doc } = {}) => {
  const name = String(card?.name || doc?.name || "").trim();
  const nodeCount = countAllLayoutNodes({
    layoutKind: doc?.layoutKind,
    slides: doc?.slides || [],
    artboards: doc?.artboards,
    nodes: doc?.nodes,
  });
  return {
    name,
    nodeCount,
    hasName: name.length > 0,
    hasLayers: Number(nodeCount) > 0,
  };
};

const resolveCreativeTemplateStatus = ({ name, nodeCount }) => {
  const trimmedName = String(name || "").trim();
  if (trimmedName && Number(nodeCount) > 0) {
    return CREATIVE_TEMPLATE_STATUS_APPROVED;
  }
  return CREATIVE_TEMPLATE_STATUS_DRAFT;
};

const getCardsPath = ({ advertiserID }) =>
  `creativeTemplatesCards/${advertiserID}`;

const getTemplatesPath = ({ advertiserID }) =>
  `creativeTemplates/${advertiserID}`;

const getPromptsPath = ({ advertiserID }) =>
  `creativeTemplatePrompts/${advertiserID}`;

const PROMPT_LIBRARY_NAME_MAX = 80;
const PROMPT_LIBRARY_PROMPT_MAX = 8000;

const listCreativeTemplatePrompts = (value) =>
  Object.entries(value || {})
    .map(([id, item]) => ({
      id,
      name: String(item?.name || "").trim(),
      prompt: String(item?.prompt || "").trim(),
      createdAt: Number(item?.createdAt) || 0,
    }))
    .filter((item) => item.id && item.name && item.prompt)
    .sort((left, right) => right.createdAt - left.createdAt);

const getTemplatePath = ({ advertiserID, templateID }) =>
  `${getTemplatesPath({ advertiserID })}/${templateID}`;

const getTemplateDocPath = ({ advertiserID, templateID }) =>
  `${getTemplatePath({ advertiserID, templateID })}/doc`;

const getAssetPath = ({ advertiserID, assetID }) =>
  `creativeAssets/${advertiserID}/${assetID}`;

const getUploadPath = ({ advertiserID, templateID }) =>
  `creative-template-assets/${advertiserID}/${templateID}/`;

const toIso = ({ timestamp }) => new Date(timestamp).toISOString();

const buildTimeFields = ({ creationTime, updateTime }) => ({
  creationTime,
  creationTimeNegative: creationTime * -1,
  updateTime,
  updateTimeNegative: updateTime * -1,
});

const resolveCreationTime = ({ card, fallback }) =>
  card?.creationTime || card?.createdAt || fallback;

const buildAudit = ({
  existingAudit = {},
  createdBy,
  creationTime,
  updateTime,
}) => ({
  createdBy: createdBy || existingAudit.createdBy,
  creationTime:
    creationTime != null
      ? toIso({ timestamp: creationTime })
      : existingAudit.creationTime || existingAudit.createdAt,
  updateTime: toIso({ timestamp: updateTime }),
});

const parseTemplateDoc = ({ doc, dispatch, functionName, templateID }) => {
  const parsed = parseCreativeTemplateDoc(doc);
  if (parsed.success) return parsed;
  console.error(`${functionName} invalidGraph`, parsed.error.issues);
  dispatch(
    LaunchError(parsed.error, false, [], {
      functionName,
      context: { templateID, issues: parsed.error.issues },
      tags: { action: "creativeTemplates", reason: "invalidGraph" },
      level: "error",
    })
  );
  return parsed;
};

const buildCard = ({
  templateID,
  advertiserID,
  name,
  status,
  thumbnailUrl,
  previewUrl = "",
  width,
  height,
  nodeCount,
  creationTime,
  updateTime,
  createdBy,
  artboard,
  artboardIDs,
  artboardFormats,
  folderID,
  layoutKind,
  slideCount,
}) => {
  const resolvedArtboard = artboard || {
    id: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
    width,
    height,
  };
  return {
    schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
    id: templateID,
    advertiserID,
    name,
    status,
    thumbnailUrl,
    ...(previewUrl ? { previewUrl } : {}),
    artboard: resolvedArtboard,
    artboardIDs: artboardIDs || [
      resolvedArtboard.id || CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
    ],
    artboardFormats: artboardFormats || [],
    nodeCount,
    ...buildTimeFields({ creationTime, updateTime }),
    createdBy,
    ...(folderID ? { folderID } : {}),
    ...(layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL ||
    layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
      ? {
          layoutKind,
          slideCount: Number(slideCount) || 0,
        }
      : {}),
  };
};

const keptReframeBackgroundPerFormat = (source = {}) =>
  source?.reframeBackgroundPerFormat === true
    ? { reframeBackgroundPerFormat: true }
    : {};

const getCardLayoutFields = ({ doc } = {}) => {
  if (
    resolveLayoutKind(doc) === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
  ) {
    return {
      layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL,
      slideCount: getContinuousSlideCount({
        layoutConfigurations: doc?.layoutConfigurations,
      }),
    };
  }
  if (!isCarouselLayout(doc)) return {};
  return {
    layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL,
    slideCount: Array.isArray(doc?.slides) ? doc.slides.length : 0,
  };
};

const getDocCardArtboards = ({ doc } = {}) => {
  const artboards = getFormatArtboards({ doc });
  if (
    resolveLayoutKind(doc) !== CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
  ) {
    return artboards;
  }
  return stripToSlideArtboards({
    artboards,
    slideCount: getContinuousSlideCount({
      layoutConfigurations: doc?.layoutConfigurations,
    }),
  });
};

const assertCanWrite = ({ role }) => {
  if (!canWriteCreativeTemplates({ role })) {
    return { ok: false, reason: "unauthorized" };
  }
  return { ok: true };
};

export const startListenerCreativeTemplateCards =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getCardsPath({ advertiserID }));
      const unsubscribe = onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });
      onReferenceAvailable(unsubscribe);

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplateCards",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplateCards =
  ({ unsubscribe } = {}) =>
  () => {
    if (typeof unsubscribe !== "function") return;
    unsubscribe();
  };

export const startListenerCreativeTemplate =
  ({
    templateID,
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(db, getTemplatePath({ advertiserID, templateID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        if (!snapshot.exists()) {
          onUpdatedDataCallback(null);
          return;
        }
        const value = snapshot.val() || {};
        const { doc, ...card } = value;
        onUpdatedDataCallback({ card, doc });
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplate =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startGetCreativeTemplate =
  ({ templateID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (!advertiserID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const snapshot = await get(
        ref(db, getTemplatePath({ advertiserID, templateID }))
      );

      if (!snapshot.exists()) {
        return { ok: false, reason: "notFound" };
      }

      const value = snapshot.val() || {};
      const { doc, ...card } = value;
      return { ok: true, data: { card, doc } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGetCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCreateEmptyCreativeTemplate =
  ({ name, folderID, layoutKind, slideCount } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) {
        return { ok: false, reason: "missingParameters" };
      }

      const templateID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const now = Date.now();
      const templateName = name ?? "";
      const width = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE;
      const height = CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE;
      const isCarousel = layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL;
      const slides = isCarousel
        ? seedCarouselSlides({
            count: slideCount,
            artboards: {
              [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
                label: "source",
                width,
                height,
                nodes: {},
                nodeLayouts: {},
              },
            },
          })
        : undefined;
      const artboards = isCarousel
        ? {}
        : {
            [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
              label: "source",
              width,
              height,
              nodeLayouts: {},
            },
          };
      const doc = {
        schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
        id: templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: {
          activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
          ...(isCarousel ? { activeSlideID: slides[0]?.id } : {}),
        },
        ...(isCarousel
          ? {
              layoutKind: CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL,
              slides,
            }
          : {}),
        assets: {},
        variables: {},
        nodes: {},
        artboards,
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };
      const card = buildCard({
        templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: "",
        width,
        height,
        nodeCount: 0,
        creationTime: now,
        updateTime: now,
        createdBy,
        folderID,
        ...getCardArtboardFields({
          artboards: isCarousel ? slides[0]?.artboards || {} : artboards,
          activeArtboardID: doc.editor.activeArtboardID,
        }),
        ...getCardLayoutFields({ doc }),
      });

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startCreateEmptyCreativeTemplate",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
        ...getCreativeTemplateFolderIndexUpdates({
          advertiserID,
          templateID,
          folderID,
        }),
      });

      return { ok: true, data: { templateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateEmptyCreativeTemplate",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "createFailed" };
    }
  };

export const startCreateCreativeTemplateFromFile =
  ({ file, name, folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      const templateID = dispatch(getKeyDatabase());
      const assetID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );

      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const { width: imageWidth, height: imageHeight } =
        await getImageDimensionsFromFile({ file });
      const { width, height } = resolveArtboardSizeFromDimensions({
        width: imageWidth,
        height: imageHeight,
      });
      const now = Date.now();
      const templateName = name || file.name.replace(/\.[^.]+$/, "");
      const doc = {
        schemaVersion: CREATIVE_TEMPLATE_SCHEMA_VERSION,
        id: templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: {
          referenceImageAssetID: assetID,
          activeArtboardID: CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
        },
        assets: {
          reference: {
            type: "image",
            assetID,
            mimeType: file.type,
            url,
            width: imageWidth,
            height: imageHeight,
            role: "editorBackdrop",
          },
        },
        variables: {},
        nodes: {},
        artboards: {
          [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
            label: "source",
            width,
            height,
            referenceAssetRef: "reference",
            nodeLayouts: {},
          },
        },
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };
      const card = buildCard({
        templateID,
        advertiserID,
        name: templateName,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: url,
        width,
        height,
        nodeCount: 0,
        creationTime: now,
        updateTime: now,
        createdBy,
        folderID,
        ...getCardArtboardFields({
          artboards: doc.artboards,
          activeArtboardID: doc.editor.activeArtboardID,
        }),
      });

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startCreateCreativeTemplateFromFile",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: { ...card, doc: parsed.data },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
        ...getCreativeTemplateFolderIndexUpdates({
          advertiserID,
          templateID,
          folderID,
        }),
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType: file.type,
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      });

      return { ok: true, data: { templateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startCreateCreativeTemplateFromFile",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

const patchTemplateAnalysis = async ({
  advertiserID,
  templateID,
  analysis,
  slideID,
}) => {
  const docPath = getTemplateDocPath({ advertiserID, templateID });
  const path = slideID
    ? `${docPath}/editor/analysisBySlide/${slideID}`
    : `${docPath}/editor/analysis`;
  await update(ref(db), {
    [path]: analysis,
  });
};

const appendIsolatedGuideUpdates = ({
  updates,
  docPath,
  previousSlides = [],
  nextSlides = [],
  previousAssets = {},
  nextAssets = {},
}) => {
  (nextSlides || []).forEach((slide) => {
    const slideIndex = (previousSlides || []).findIndex(
      (item) => item?.id && item.id === slide?.id
    );
    if (slideIndex < 0) return;
    Object.entries(slide?.artboards || {}).forEach(([artboardID, artboard]) => {
      const nextRef = artboard?.referenceAssetRef;
      const previousRef =
        previousSlides[slideIndex]?.artboards?.[artboardID]?.referenceAssetRef;
      if (!nextRef || nextRef === previousRef) return;
      updates[
        `${docPath}/slides/${slideIndex}/artboards/${artboardID}/referenceAssetRef`
      ] = nextRef;
    });
  });
  Object.entries(nextAssets || {}).forEach(([key, asset]) => {
    if (!asset?.url) return;
    const previous = previousAssets?.[key];
    if (previous?.url === asset.url && previous?.assetID === asset.assetID) {
      return;
    }
    updates[`${docPath}/assets/${key}`] = asset;
  });
};

const withIsolatedCarouselGuides = (doc = {}) => {
  if (!isCarouselLayout(doc)) return doc;
  const isolated = isolateCarouselSlideGuides({
    slides: doc.slides || [],
    assets: doc.assets || {},
  });
  if (!isolated.changed) return doc;
  return {
    ...doc,
    slides: isolated.slides,
    assets: isolated.assets,
  };
};

export const startAttachOriginCreativeAndAnalyze =
  ({ templateID, file, artboardID, slideID, extractText = false } = {}) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    let hasActivityNotification = false;
    const payloadSnapshot = { templateID };

    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const existingDoc = withIsolatedCarouselGuides(current.data.doc || {});
      const isCarousel = isCarouselLayout(existingDoc);
      const requestedSlideID = String(slideID || "").trim();
      const targetSlideID = isCarousel
        ? requestedSlideID ||
          existingDoc.editor?.activeSlideID ||
          existingDoc.slides?.[0]?.id ||
          ""
        : "";
      if (
        targetSlideID &&
        existingDoc.editor?.analysisBySlide?.[targetSlideID]?.status ===
          CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING
      ) {
        return { ok: false, reason: "analyzing" };
      }
      const existingArtboards = isCarousel
        ? getSlideWorkingArtboards({
            doc: existingDoc,
            slideID: targetSlideID,
          })
        : existingDoc.artboards || {};
      const requestedArtboardID = String(artboardID || "").trim();
      const referenceArtboardID = existingArtboards[requestedArtboardID]
        ? requestedArtboardID
        : findReferenceArtboardID({
            artboards: existingArtboards,
            assets: existingDoc.assets,
            reference: isCarousel ? null : existingDoc.assets?.reference,
          });
      const targetArtboardID = existingArtboards[referenceArtboardID]
        ? referenceArtboardID
        : CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
      const targetArtboard = existingArtboards[targetArtboardID] || {};
      const preserveArtboardSize =
        Object.keys(existingArtboards).length > 1 &&
        Number(targetArtboard.width) > 0 &&
        Number(targetArtboard.height) > 0;
      const assetKey = getReferenceAssetKey({
        artboardID: targetArtboardID,
        slideID: targetSlideID,
      });
      const existingReference =
        existingDoc.assets?.[targetArtboard.referenceAssetRef] ||
        existingDoc.assets?.[assetKey] ||
        {};
      let url = existingReference.url;
      let width =
        existingReference.width ||
        targetArtboard.width ||
        existingArtboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.width;
      let height =
        existingReference.height ||
        targetArtboard.height ||
        existingArtboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.height;
      let mimeType = existingReference.mimeType;
      let assetID = existingReference.assetID;

      if (file) {
        const dimensions = await getImageDimensionsFromFile({ file });
        width = dimensions.width;
        height = dimensions.height;
        mimeType = file.type;
        if (isLayerizeImageTooSmall({ width, height })) {
          return { ok: false, reason: "imageTooSmall" };
        }
        assetID = assetID || dispatch(getKeyDatabase());
        url = await dispatch(
          startUploadFileAws(
            file,
            file.type,
            getUploadPath({ advertiserID, templateID }),
            true,
            true
          )
        );
        if (!url) {
          return { ok: false, reason: "uploadFailed" };
        }
      } else if (isLayerizeImageTooSmall({ width, height })) {
        return { ok: false, reason: "imageTooSmall" };
      }

      if (!url || !assetID || !width || !height) {
        return { ok: false, reason: "missingParameters" };
      }
      if (
        referenceAssetIDIsShared({
          assets: existingDoc.assets,
          assetID,
          exceptKey: assetKey,
        })
      ) {
        assetID = dispatch(getKeyDatabase());
      }

      const { width: matchedWidth, height: matchedHeight } =
        resolveArtboardSizeFromDimensions({ width, height });
      const artboardWidth = preserveArtboardSize
        ? Number(targetArtboard.width)
        : matchedWidth;
      const artboardHeight = preserveArtboardSize
        ? Number(targetArtboard.height)
        : matchedHeight;
      const now = Date.now();
      const nextReference = {
        type: "image",
        assetID,
        mimeType,
        url,
        width,
        height,
        role: "editorBackdrop",
      };
      const artboardsForDoc =
        Object.keys(existingArtboards).length > 0
          ? existingArtboards
          : {
              [CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]: {
                label: "source",
                width: artboardWidth,
                height: artboardHeight,
                nodeLayouts: {},
              },
            };
      const nextTargetArtboard = {
        ...(artboardsForDoc[targetArtboardID] || {}),
        width: artboardWidth,
        height: artboardHeight,
        referenceAssetRef: assetKey,
        nodeLayouts: artboardsForDoc[targetArtboardID]?.nodeLayouts || {},
      };
      const doc = {
        ...existingDoc,
        editor: {
          ...(existingDoc.editor || {}),
          activeArtboardID: targetArtboardID,
          ...(isCarousel ? {} : { referenceImageAssetID: assetID }),
          ...(isCarousel && targetSlideID
            ? {
                analysisBySlide: {
                  ...(existingDoc.editor?.analysisBySlide || {}),
                  [targetSlideID]: {
                    status: CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING,
                  },
                },
              }
            : {
                analysis: {
                  status: CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING,
                },
              }),
        },
        assets: {
          ...(existingDoc.assets || {}),
          [assetKey]: nextReference,
        },
        ...(isCarousel
          ? { artboards: {}, nodes: {} }
          : {
              artboards: {
                ...artboardsForDoc,
                [targetArtboardID]: nextTargetArtboard,
              },
            }),
        ...(isCarousel && targetSlideID
          ? {
              slides: (existingDoc.slides || []).map((slide) =>
                slide?.id === targetSlideID
                  ? {
                      ...slide,
                      artboards: {
                        ...(slide.artboards || {}),
                        [targetArtboardID]: {
                          ...(slide.artboards?.[targetArtboardID] ||
                            nextTargetArtboard),
                          width: artboardWidth,
                          height: artboardHeight,
                          referenceAssetRef: assetKey,
                        },
                      },
                    }
                  : slide
              ),
            }
          : {}),
        audit: buildAudit({
          existingAudit: existingDoc.audit,
          updateTime: now,
        }),
      };

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startAttachOriginCreativeAndAnalyze",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const existingCard = current.data.card || {};
      const card = buildCard({
        templateID,
        advertiserID,
        name: existingCard.name,
        status: existingCard.status,
        thumbnailUrl: url,
        width,
        height,
        nodeCount: existingCard.nodeCount || 0,
        creationTime: resolveCreationTime({
          card: existingCard,
          fallback: now,
        }),
        updateTime: now,
        createdBy: existingCard.createdBy,
        folderID: existingCard.folderID,
        ...getCardArtboardFields({
          artboards: getDocCardArtboards({ doc: parsed.data }),
          activeArtboardID: parsed.data.editor?.activeArtboardID,
        }),
      });

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: {
          ...card,
          ...keptReframeBackgroundPerFormat(existingCard),
          doc: parsed.data,
        },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType,
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      });

      hasActivityNotification = true;
      dispatch(
        addNotification({
          type: "analyzingCreativeTemplate",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          }),
          generationData: {
            ...payloadSnapshot,
            imageUrl: url,
            isGenerating: true,
          },
        })
      );

      const data = await createCreativeJobAndWaitForResponse({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
        payload: {
          templateID,
          imageUrl: url,
          artboardID: targetArtboardID,
          extractText: Boolean(extractText),
          ...(targetSlideID ? { slideID: targetSlideID } : {}),
        },
        onJobCreated: ({ jobID }) => {
          dispatch(
            promoteCreativeJobNotification({ fromId: notificationId, jobID })
          );
          notificationId = jobID;
          const docPath = getTemplateDocPath({ advertiserID, templateID });
          const jobPath = targetSlideID
            ? `${docPath}/editor/analysisBySlide/${targetSlideID}/jobID`
            : `${docPath}/editor/analysis/jobID`;
          update(ref(db), {
            [jobPath]: jobID,
          }).catch((error) => {
            dispatch(
              LaunchError(error, false, [], {
                functionName: "startAnalyzeCreativeTemplate",
                capture: "exception",
                context: { templateID, jobPath },
                tags: { source: "rtdb" },
              })
            );
          });
        },
      });

      const latest = await dispatch(startGetCreativeTemplate({ templateID }));
      const latestDoc = latest.ok ? latest.data?.doc : null;
      const latestCard = latest.ok ? latest.data?.card : null;
      const latestSlide =
        targetSlideID && Array.isArray(latestDoc?.slides)
          ? latestDoc.slides.find((slide) => slide?.id === targetSlideID)
          : null;
      const analyzedNodes =
        latestSlide?.artboards?.[targetArtboardID]?.nodes ||
        latestDoc?.artboards?.[targetArtboardID]?.nodes ||
        latestDoc?.nodes ||
        null;
      const analyzedLayouts =
        latestSlide?.artboards?.[targetArtboardID]?.nodeLayouts ||
        latestDoc?.artboards?.[targetArtboardID]?.nodeLayouts ||
        null;
      const analyzedNodeCount = countAllLayoutNodes({
        layoutKind: latestDoc?.layoutKind,
        slides: latestDoc?.slides || [],
        artboards: isCarousel ? {} : latestDoc?.artboards,
        nodes: isCarousel ? {} : latestDoc?.nodes,
      });
      const nextStatus = resolveCreativeTemplateStatus({
        name: latestCard?.name,
        nodeCount: analyzedNodeCount,
      });
      const afterAnalyzeUpdates = {
        [`${getTemplatePath({ advertiserID, templateID })}/status`]: nextStatus,
        [`${getTemplatePath({ advertiserID, templateID })}/nodeCount`]:
          analyzedNodeCount,
        [`${getCardsPath({ advertiserID })}/${templateID}/status`]: nextStatus,
        [`${getCardsPath({ advertiserID })}/${templateID}/nodeCount`]:
          analyzedNodeCount,
      };
      if (analyzedNodes) {
        const docPath = getTemplateDocPath({ advertiserID, templateID });
        if (isCarousel) {
          afterAnalyzeUpdates[`${docPath}/artboards`] = {};
          afterAnalyzeUpdates[`${docPath}/nodes`] = {};
        } else {
          afterAnalyzeUpdates[
            `${docPath}/artboards/${targetArtboardID}/nodes`
          ] = analyzedNodes;
          if (analyzedLayouts) {
            afterAnalyzeUpdates[
              `${docPath}/artboards/${targetArtboardID}/nodeLayouts`
            ] = analyzedLayouts;
          }
        }
        const slideIndex = findSlideIndex({
          slides: latestDoc?.slides,
          slideID: targetSlideID,
        });
        if (isCarousel && slideIndex >= 0) {
          afterAnalyzeUpdates[
            `${docPath}/slides/${slideIndex}/artboards/${targetArtboardID}/nodes`
          ] = analyzedNodes;
          if (analyzedLayouts) {
            afterAnalyzeUpdates[
              `${docPath}/slides/${slideIndex}/artboards/${targetArtboardID}/nodeLayouts`
            ] = analyzedLayouts;
          }
        }
      }
      await update(ref(db), afterAnalyzeUpdates);

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.SUCCESS,
          progress: 100,
          generationData: mapJobResultToGenerationData({
            job: {
              jobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
              status: CREATIVE_JOB_STATUS.SUCCESS,
              response: data,
            },
            payloadSnapshot,
          }),
        })
      );

      return { ok: true, data: { templateID } };
    } catch (error) {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      if (advertiserID && templateID) {
        await patchTemplateAnalysis({
          advertiserID,
          templateID,
          slideID: String(slideID || "").trim() || undefined,
          analysis: {
            status: CREATIVE_TEMPLATE_ANALYSIS_STATUS.ERROR,
            errorCode: error?.code || "ERROR_OCCURRED",
          },
        }).catch((error) => {
          dispatch(
            LaunchError(error, false, [], {
              functionName: "patchTemplateAnalysis",
              capture: "exception",
              context: { templateID },
              tags: { source: "rtdb" },
            })
          );
        });
      }

      const errorFormatted = dispatch(
        LaunchError(error, !hasActivityNotification, [], {
          functionName: "startAttachOriginCreativeAndAnalyze",
          context: { templateID },
          creativeJobType: CREATIVE_JOB_TYPES.analyzeCreativeTemplate,
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted?.message || error?.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      return { ok: false, reason: "analyzeFailed" };
    }
  };

export const startUploadCreativeTemplateLayerAsset =
  ({ templateID, file }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      await getImageDimensionsFromFile({ file });
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const assetID = dispatch(getKeyDatabase());

      return {
        ok: true,
        data: {
          assetID,
          url,
          mimeType: file.type || "image/png",
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUploadCreativeTemplateLayerAsset",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

export const startAttachArtboardReference =
  ({
    templateID,
    artboardID,
    file,
    artboard,
    artboards,
    slideID,
    guideSlideID,
  } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID || !templateID || !artboardID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const storedDoc = current.data.doc || {};
      const existingDoc = withIsolatedCarouselGuides(storedDoc);
      const explicitSlideID = String(slideID || "").trim();
      const perSlideGuideID = String(guideSlideID || "").trim();
      const hasExplicitSlide =
        Boolean(explicitSlideID) &&
        Array.isArray(existingDoc.slides) &&
        existingDoc.slides.some((slide) => slide?.id === explicitSlideID);
      const isCarousel = isCarouselLayout(existingDoc) || hasExplicitSlide;
      const targetSlideID = isCarousel
        ? explicitSlideID ||
          existingDoc.editor?.activeSlideID ||
          existingDoc.slides?.[0]?.id ||
          ""
        : "";
      const existingArtboards = isCarousel
        ? getSlideWorkingArtboards({
            doc: existingDoc,
            slideID: targetSlideID,
          })
        : existingDoc.artboards || {};
      const sourceArtboard =
        artboard ||
        (artboards || {})[artboardID] ||
        existingArtboards[artboardID];
      if (!sourceArtboard) {
        return { ok: false, reason: "invalidGraph" };
      }

      const assetKey = getReferenceAssetKey({
        artboardID,
        slideID: perSlideGuideID || targetSlideID,
      });
      const previousGuideKey = perSlideGuideID
        ? sourceArtboard.slideGuides?.[perSlideGuideID]
        : sourceArtboard.referenceAssetRef;
      const existingAsset =
        existingDoc.assets?.[assetKey] ||
        (previousGuideKey ? existingDoc.assets?.[previousGuideKey] : null) ||
        {};
      let assetID = existingAsset.assetID || dispatch(getKeyDatabase());
      if (
        referenceAssetIDIsShared({
          assets: existingDoc.assets,
          assetID,
          exceptKey: assetKey,
        })
      ) {
        assetID = dispatch(getKeyDatabase());
      }
      const url = await dispatch(
        startUploadFileAws(
          file,
          file.type,
          getUploadPath({ advertiserID, templateID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const { width, height } = await getImageDimensionsFromFile({ file });
      const now = Date.now();
      const nextAsset = {
        type: "image",
        assetID,
        mimeType: file.type || "image/png",
        url,
        width,
        height,
        role: "editorBackdrop",
      };
      const nextAssets = {
        ...(existingDoc.assets || {}),
        [assetKey]: nextAsset,
      };
      const hydratedArtboards = hydrateArtboards({
        artboards: {
          ...existingArtboards,
          ...(artboards || {}),
          [artboardID]: {
            ...(existingArtboards[artboardID] || {}),
            ...sourceArtboard,
            ...(perSlideGuideID
              ? {
                  slideGuides: {
                    ...(sourceArtboard.slideGuides || {}),
                    [perSlideGuideID]: assetKey,
                  },
                }
              : { referenceAssetRef: assetKey }),
          },
        },
        nodes: isCarousel ? {} : existingDoc.nodes || {},
        allowSharedNodes: !isCarousel,
      });
      const graph = sanitizeArtboardsForPersist({
        artboards: hydratedArtboards,
      });
      const nextArtboard = {
        ...graph.artboards[artboardID],
        ...(perSlideGuideID
          ? {
              slideGuides: {
                ...(graph.artboards[artboardID]?.slideGuides || {}),
                [perSlideGuideID]: assetKey,
              },
            }
          : { referenceAssetRef: assetKey }),
        nodes: sanitizeNodesForPersist({
          nodes: graph.artboards[artboardID]?.nodes || {},
          isRoot: true,
        }),
      };
      const nextArtboards = {
        ...graph.artboards,
        [artboardID]: nextArtboard,
      };
      const nextEditor = {
        ...(existingDoc.editor || {}),
        activeArtboardID: resolveActiveArtboardID({
          artboards: nextArtboards,
          activeArtboardID: artboardID,
        }),
      };
      if (!isCarousel && assetKey === "reference") {
        nextEditor.referenceImageAssetID = assetID;
      }
      const nextSlides =
        isCarousel && targetSlideID
          ? (existingDoc.slides || []).map((slide) =>
              slide?.id === targetSlideID
                ? {
                    ...slide,
                    artboards: {
                      ...(slide.artboards || {}),
                      [artboardID]: nextArtboard,
                    },
                  }
                : slide
            )
          : existingDoc.slides;

      const parsed = parseTemplateDoc({
        doc: {
          ...existingDoc,
          editor: nextEditor,
          assets: nextAssets,
          artboards: isCarousel ? {} : nextArtboards,
          nodes: isCarousel ? {} : existingDoc.nodes,
          ...(isCarousel ? { slides: nextSlides } : {}),
          audit: buildAudit({
            existingAudit: existingDoc.audit,
            updateTime: now,
          }),
        },
        dispatch,
        functionName: "startAttachArtboardReference",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const docPath = getTemplateDocPath({ advertiserID, templateID });
      const templatePath = getTemplatePath({ advertiserID, templateID });
      const persistArtboard = isCarousel
        ? nextArtboard
        : parsed.data.artboards[artboardID];
      const slideIndex = findSlideIndex({
        slides: existingDoc.slides,
        slideID: targetSlideID,
      });
      const updates = {
        [`${docPath}/audit`]: parsed.data.audit,
        [getAssetPath({ advertiserID, assetID })]: {
          id: assetID,
          advertiserID,
          type: "image",
          url,
          mimeType: file.type || "image/png",
          width,
          height,
          role: "editorBackdrop",
          creationTime: now,
          creationTimeNegative: now * -1,
        },
      };
      if (isCarousel) {
        appendIsolatedGuideUpdates({
          updates,
          docPath,
          previousSlides: storedDoc.slides,
          nextSlides: existingDoc.slides,
          previousAssets: storedDoc.assets,
          nextAssets: existingDoc.assets,
        });
      }
      updates[`${docPath}/assets/${assetKey}`] = nextAsset;

      if (isCarousel && slideIndex >= 0) {
        updates[`${docPath}/artboards`] = {};
        updates[`${docPath}/nodes`] = {};
        updates[
          `${docPath}/slides/${slideIndex}/artboards/${artboardID}/referenceAssetRef`
        ] = assetKey;
        updates[`${templatePath}/thumbnailUrl`] = url;
        updates[
          `${getCardsPath({ advertiserID })}/${templateID}/thumbnailUrl`
        ] = url;
      } else if (perSlideGuideID && existingArtboards[artboardID]) {
        updates[
          `${docPath}/artboards/${artboardID}/slideGuides/${perSlideGuideID}`
        ] = assetKey;
      } else if (existingArtboards[artboardID]) {
        updates[`${docPath}/artboards/${artboardID}/referenceAssetRef`] =
          assetKey;
      } else {
        updates[`${docPath}/artboards/${artboardID}`] = persistArtboard;
      }

      if (!isCarousel && assetKey === "reference") {
        updates[`${docPath}/editor/referenceImageAssetID`] = assetID;
        updates[`${templatePath}/thumbnailUrl`] = url;
        updates[
          `${getCardsPath({ advertiserID })}/${templateID}/thumbnailUrl`
        ] = url;
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          templateID,
          artboardID,
          assetKey,
          asset: nextAsset,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startAttachArtboardReference",
          context: { templateID, artboardID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };

export const startColdBootCreativeTemplatePreview = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    await axios({
      method: "post",
      url: `${getCloudFunctionUrl(
        "renderCreativeTemplatePreview"
      )}/renderCreativeTemplatePreview`,
      data: { coldBoot: true },
      headers: { Authorization: `Bearer ${token}` },
    });
    return { ok: true };
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startColdBootCreativeTemplatePreview",
        capture: "exception",
      })
    );
    console.error("startColdBootCreativeTemplatePreview failed", error);
    return { ok: false };
  }
};

export const startSaveCreativeTemplate =
  ({
    templateID,
    name,
    nodes,
    variables,
    assets,
    artboards,
    slides,
    layoutKind,
    layoutConfigurations,
    activeArtboardID,
    activeSlideID,
  }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const existingDoc = current.data.doc || {};
      const processingSlideIDs = listProcessingSlideIDs({
        analysisBySlide: existingDoc.editor?.analysisBySlide,
      });
      const canSaveContinuous =
        CREATIVE_TEMPLATE_CONTINUOUS_CAROUSEL_ENABLED ||
        existingDoc.layoutKind ===
          CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;
      const requestedLayoutKind =
        layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE ||
        layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL ||
        (layoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL &&
          canSaveContinuous)
          ? layoutKind
          : existingDoc.layoutKind;
      const requestedSlides =
        requestedLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.SINGLE ||
        requestedLayoutKind ===
          CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
          ? []
          : Array.isArray(slides)
            ? slides
            : existingDoc.slides;
      const nextLayoutKind = resolveLayoutKind({
        layoutKind: requestedLayoutKind,
        slides: requestedSlides,
      });
      const savingCarousel =
        nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL;
      if (
        !savingCarousel &&
        existingDoc.editor?.analysis?.status ===
          CREATIVE_TEMPLATE_ANALYSIS_STATUS.PROCESSING
      ) {
        return { ok: false, reason: "analyzing" };
      }
      if (
        savingCarousel &&
        processingSlideIDs.includes(
          String(activeSlideID || existingDoc.editor?.activeSlideID || "")
        )
      ) {
        return { ok: false, reason: "analyzing" };
      }
      const now = Date.now();
      const trimmedName = String(name ?? existingDoc.name ?? "").trim();
      if (!trimmedName) {
        return { ok: false, reason: "nameRequired" };
      }
      const nextName = trimmedName;
      const flushedLayout =
        nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
          ? flushActiveCell({
              layoutKind: nextLayoutKind,
              slides: requestedSlides || [],
              artboards: artboards || existingDoc.artboards || {},
              activeSlideID: activeSlideID || existingDoc.editor?.activeSlideID,
              activeArtboardID:
                activeArtboardID || existingDoc.editor?.activeArtboardID,
              nodes,
              nodeLayouts: undefined,
            })
          : {
              slides: [],
              artboards: artboards || existingDoc.artboards || {},
            };
      const hydratedArtboards = hydrateArtboards({
        artboards: flushedLayout.artboards,
        nodes: savingCarousel ? {} : nodes || existingDoc.nodes || {},
        allowSharedNodes: !savingCarousel,
      });
      const resolvedActiveID = resolveActiveArtboardID({
        artboards: hydratedArtboards,
        activeArtboardID:
          activeArtboardID || existingDoc.editor?.activeArtboardID,
      });
      const artboardsWithActive = nodes
        ? {
            ...hydratedArtboards,
            [resolvedActiveID]: {
              ...(hydratedArtboards[resolvedActiveID] || {}),
              nodes,
            },
          }
        : hydratedArtboards;
      const exceedsCap = Object.values(artboardsWithActive).some(
        (artboard) =>
          countNodes({ nodes: artboard?.nodes || {} }) >
          CREATIVE_TEMPLATE_MAX_NODES
      );
      if (exceedsCap) {
        return { ok: false, reason: "invalidGraph" };
      }

      const graph = sanitizeArtboardsForPersist({
        artboards: artboardsWithActive,
      });
      const nextActiveArtboardID = graph.artboards[resolvedActiveID]
        ? resolvedActiveID
        : Object.keys(graph.artboards)[0] ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID;
      const mirroredNodes =
        graph.artboards[nextActiveArtboardID]?.nodes ||
        graph.artboards[CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID]?.nodes ||
        {};
      const nextActiveSlideID =
        nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
          ? activeSlideID ||
            existingDoc.editor?.activeSlideID ||
            flushedLayout.slides?.[0]?.id
          : undefined;
      const persistedSlides =
        nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
          ? mergeAnalyzingSlides({
              slides: (flushedLayout.slides || []).map((slide) => {
                const slideArtboards =
                  slide?.id === nextActiveSlideID
                    ? graph.artboards
                    : slide?.artboards || {};
                return {
                  ...slide,
                  artboards: sanitizeArtboardsForPersist({
                    artboards: slideArtboards,
                  }).artboards,
                };
              }),
              existingSlides: existingDoc.slides,
              processingSlideIDs,
            })
          : undefined;
      const isolatedGuides = savingCarousel
        ? isolateCarouselSlideGuides({
            slides: persistedSlides || [],
            assets: assets || existingDoc.assets || {},
          })
        : null;
      const slidesToSave = isolatedGuides?.changed
        ? isolatedGuides.slides
        : persistedSlides;
      const assetsToSave = isolatedGuides?.changed
        ? isolatedGuides.assets
        : assets || existingDoc.assets;
      const nodeCount = countAllLayoutNodes({
        layoutKind: nextLayoutKind,
        slides: slidesToSave || [],
        artboards: graph.artboards,
        nodes: mirroredNodes,
      });
      const nextStatus = resolveCreativeTemplateStatus({
        name: nextName,
        nodeCount,
      });
      const doc = {
        ...existingDoc,
        name: nextName,
        status: nextStatus,
        nodes: savingCarousel ? {} : mirroredNodes,
        variables: variables || {},
        assets: assetsToSave,
        artboards: savingCarousel ? {} : graph.artboards,
        editor: {
          ...(existingDoc.editor || {}),
          activeArtboardID: nextActiveArtboardID,
          ...(nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL
            ? { activeSlideID: nextActiveSlideID }
            : {}),
        },
        audit: buildAudit({
          existingAudit: existingDoc.audit,
          updateTime: now,
        }),
      };
      if (nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL) {
        doc.layoutKind = CREATIVE_TEMPLATE_LAYOUT_KIND.CAROUSEL;
        doc.slides = slidesToSave;
      } else {
        delete doc.layoutKind;
        delete doc.slides;
        if (doc.editor) {
          delete doc.editor.activeSlideID;
        }
      }
      if (
        nextLayoutKind === CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL
      ) {
        doc.layoutKind = CREATIVE_TEMPLATE_LAYOUT_KIND.CONTINUOUS_CAROUSEL;
        doc.layoutConfigurations = {
          ...(existingDoc.layoutConfigurations || {}),
          continuousCarousel: {
            slideCount: (
              layoutConfigurations || existingDoc.layoutConfigurations
            )?.continuousCarousel?.slideCount,
          },
        };
      } else {
        delete doc.layoutConfigurations;
      }

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startSaveCreativeTemplate",
        templateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const existingCard = current.data.card || {};
      const cardFields = getCardArtboardFields({
        artboards: getDocCardArtboards({ doc: parsed.data }),
        activeArtboardID: parsed.data.editor?.activeArtboardID,
      });
      const cardThumbnailRef = getDocCardArtboards({ doc: parsed.data })[
        parsed.data.editor?.activeArtboardID
      ]?.referenceAssetRef;
      const card = buildCard({
        templateID,
        advertiserID,
        name: nextName,
        status: nextStatus,
        thumbnailUrl:
          parsed.data.assets?.[cardThumbnailRef]?.url ||
          parsed.data.assets?.reference?.url ||
          existingCard.thumbnailUrl ||
          "",
        previewUrl: existingCard.previewUrl || "",
        width: cardFields.artboard.width,
        height: cardFields.artboard.height,
        nodeCount: countAllLayoutNodes({
          layoutKind: parsed.data.layoutKind,
          slides: parsed.data.slides || [],
          artboards: parsed.data.artboards,
          nodes: parsed.data.nodes,
        }),
        creationTime: resolveCreationTime({
          card: existingCard,
          fallback: now,
        }),
        updateTime: now,
        createdBy: existingCard.createdBy,
        folderID: existingCard.folderID,
        ...cardFields,
        ...getCardLayoutFields({ doc: parsed.data }),
      });

      const templatePath = getTemplatePath({ advertiserID, templateID });
      await update(ref(db), {
        [templatePath]: {
          ...card,
          ...keptReframeBackgroundPerFormat(existingCard),
          doc: parsed.data,
        },
        [`${getCardsPath({ advertiserID })}/${templateID}`]: card,
      });

      try {
        const token = await dispatch(renewToken());
        axios({
          method: "post",
          url: `${getCloudFunctionUrl(
            "renderCreativeTemplatePreview"
          )}/renderCreativeTemplatePreview`,
          data: { templateID },
          headers: { Authorization: `Bearer ${token}` },
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (previewError) {
        dispatch(
          LaunchError(previewError, false, [], {
            functionName: "startSaveCreativeTemplate",
            capture: "exception",
          })
        );
        console.error("renderCreativeTemplatePreview failed", previewError);
      }

      return { ok: true, data: { templateID, status: nextStatus } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "saveFailed" };
    }
  };

export const startDuplicateCreativeTemplate =
  ({ templateID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) return current;

      const newTemplateID = dispatch(getKeyDatabase());
      const createdBy = dispatch(getUserID());
      const now = Date.now();
      const sourceDoc = current.data.doc || {};
      const sourceCard = current.data.card || {};
      const backdropEntries = listBackdropAssets({
        assets: sourceDoc.assets,
        artboards: sourceDoc.artboards,
        slides: sourceDoc.slides,
      });
      const nextAssets = { ...(sourceDoc.assets || {}) };
      const backdropWrites = backdropEntries.map(({ key, asset }) => {
        const newAssetID = dispatch(getKeyDatabase());
        nextAssets[key] = {
          ...asset,
          assetID: newAssetID,
          role: "editorBackdrop",
        };
        return { assetID: newAssetID, asset: nextAssets[key] };
      });
      const hasReference = Boolean(nextAssets.reference?.url);

      const nextEditor = {
        ...(sourceDoc.editor || {}),
        activeArtboardID:
          sourceDoc.editor?.activeArtboardID ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_ID,
      };
      delete nextEditor.analysis;
      delete nextEditor.analysisBySlide;
      if (hasReference && nextAssets.reference?.assetID) {
        nextEditor.referenceImageAssetID = nextAssets.reference.assetID;
      } else {
        delete nextEditor.referenceImageAssetID;
      }

      const doc = {
        ...sourceDoc,
        id: newTemplateID,
        advertiserID,
        name: `${sourceDoc.name} copy`,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        editor: nextEditor,
        assets: nextAssets,
        ...(isCarouselLayout(sourceDoc) ? { artboards: {}, nodes: {} } : {}),
        audit: buildAudit({ createdBy, creationTime: now, updateTime: now }),
      };

      const parsed = parseTemplateDoc({
        doc,
        dispatch,
        functionName: "startDuplicateCreativeTemplate",
        templateID: newTemplateID,
      });
      if (!parsed.success) {
        return { ok: false, reason: "invalidGraph" };
      }

      const card = buildCard({
        templateID: newTemplateID,
        advertiserID,
        name: doc.name,
        status: CREATIVE_TEMPLATE_STATUS_DRAFT,
        thumbnailUrl: sourceCard.thumbnailUrl || "",
        previewUrl: sourceCard.previewUrl || "",
        width:
          sourceCard.artboard?.width ||
          nextAssets.reference?.width ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
        height:
          sourceCard.artboard?.height ||
          nextAssets.reference?.height ||
          CREATIVE_TEMPLATE_DEFAULT_ARTBOARD_SIZE,
        nodeCount: countAllLayoutNodes({
          layoutKind: parsed.data.layoutKind,
          slides: parsed.data.slides || [],
          artboards: isCarouselLayout(parsed.data) ? {} : parsed.data.artboards,
          nodes: isCarouselLayout(parsed.data) ? {} : parsed.data.nodes,
        }),
        creationTime: now,
        updateTime: now,
        createdBy,
        folderID: sourceCard.folderID,
        ...getCardArtboardFields({
          artboards: getDocCardArtboards({ doc: parsed.data }),
          activeArtboardID: parsed.data.editor?.activeArtboardID,
        }),
        ...getCardLayoutFields({ doc: parsed.data }),
      });

      const templatePath = getTemplatePath({
        advertiserID,
        templateID: newTemplateID,
      });
      const updates = {
        [templatePath]: {
          ...card,
          ...keptReframeBackgroundPerFormat(sourceCard),
          doc: parsed.data,
        },
        [`${getCardsPath({ advertiserID })}/${newTemplateID}`]: card,
        ...getCreativeTemplateFolderIndexUpdates({
          advertiserID,
          templateID: newTemplateID,
          folderID: sourceCard.folderID,
        }),
      };
      if (backdropWrites.length) {
        backdropWrites.forEach(({ assetID, asset }) => {
          updates[getAssetPath({ advertiserID, assetID })] = {
            id: assetID,
            advertiserID,
            type: "image",
            url: asset.url,
            mimeType: asset.mimeType,
            width: asset.width,
            height: asset.height,
            role: "editorBackdrop",
            creationTime: now,
            creationTimeNegative: now * -1,
          };
        });
      }
      await update(ref(db), updates);

      return { ok: true, data: { templateID: newTemplateID } };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDuplicateCreativeTemplate",
          context: { templateID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteCreativeTemplate =
  ({
    templateID,
    mode = CREATIVE_TEMPLATE_DELETE_MODE.DELETE,
    name,
    nodeCount,
  } = {}) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!templateID) {
        return { ok: false, reason: "missingParameters" };
      }

      dispatch(hideCreativeTemplatePendingDelete({ templateID }));

      const current = await dispatch(startGetCreativeTemplate({ templateID }));
      if (!current.ok) {
        if (
          mode === CREATIVE_TEMPLATE_DELETE_MODE.DISCARD_DRAFT &&
          current.reason === "notFound"
        ) {
          return { ok: true };
        }
        dispatch(clearCreativeTemplatePendingDelete({ templateID }));
        return current;
      }

      const serverView = resolveTemplateNameAndNodeCount({
        card: current.data.card,
        doc: current.data.doc,
      });
      const clientName =
        name !== undefined ? String(name || "").trim() : serverView.name;
      const clientNodeCount =
        nodeCount !== undefined ? Number(nodeCount) || 0 : serverView.nodeCount;
      const clientHasName = clientName.length > 0;
      const clientHasLayers = clientNodeCount > 0;

      if (mode === CREATIVE_TEMPLATE_DELETE_MODE.DISCARD_DRAFT) {
        if (
          serverView.hasName ||
          serverView.hasLayers ||
          clientHasName ||
          clientHasLayers
        ) {
          dispatch(clearCreativeTemplatePendingDelete({ templateID }));
          return { ok: false, reason: "discardBlocked" };
        }
      }

      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "deleteCreativeTemplate"
        )}/deleteCreativeTemplate`,
        data: {
          templateID,
          mode,
          name: clientName,
          nodeCount: clientNodeCount,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!result?.ok) {
        dispatch(clearCreativeTemplatePendingDelete({ templateID }));
        return {
          ok: false,
          reason: result?.code || "deleteFailed",
          message: result?.message,
        };
      }
      return { ok: true, data: result.data };
    } catch (error) {
      dispatch(clearCreativeTemplatePendingDelete({ templateID }));
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteCreativeTemplate",
          context: { templateID, mode },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "deleteFailed" };
    }
  };

const serializeProduceRows = ({ rows = [], columns = [] }) =>
  rows.map((row, index) => {
    if (columns.length === 0) {
      return {
        variationIndex: row?.variationIndex || String(index + 1),
      };
    }
    const next = {};
    columns.forEach((column) => {
      next[column.key] = row?.[column.key] ?? "";
    });
    return next;
  });

export const startProduceCreativeTemplate =
  ({
    templateID,
    produceID,
    language,
    rows,
    columns,
    artboardIDs = [],
    slideIDs = [],
    cellCount: requestedCellCount,
    variationCount,
    jobRows,
    jobVariationCount,
    intent,
  }) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    let hasActivityNotification = false;
    const persistedRows = serializeProduceRows({ rows, columns });
    const serializedJobRows = serializeProduceRows({
      rows: jobRows || rows,
      columns,
    });
    const persistedColumns = serializeProduceColumns({ columns });
    const itemCount = serializedJobRows.length;
    const hasVariables = (columns || []).length > 0;
    const variationsPerRow = getProduceVariationsPerRow({
      variationCount: jobVariationCount ?? variationCount,
      hasVariables,
    });
    const selectedArtboardIDs = Array.isArray(artboardIDs)
      ? artboardIDs.filter(Boolean)
      : [];
    const selectedSlideIDs = Array.isArray(slideIDs)
      ? slideIDs.filter(Boolean)
      : [];
    const artboardCount = Math.max(selectedArtboardIDs.length, 1);
    const inferredSlideCount = Math.max(selectedSlideIDs.length, 1);
    const cellCount =
      Number(requestedCellCount) > 0
        ? Number(requestedCellCount)
        : artboardCount * inferredSlideCount;
    const productionRowCount = itemCount * variationsPerRow;
    const assetCount = productionRowCount * cellCount;
    const parsedVariationCount = Number(variationCount);
    const parsedJobVariationCount = Number(jobVariationCount ?? variationCount);
    const payloadSnapshot = {
      templateID,
      produceID,
      language,
      itemCount: assetCount,
      artboardIDs: selectedArtboardIDs,
      artboardCount,
      cellCount,
      ...(selectedSlideIDs.length > 0 ? { slideIDs: selectedSlideIDs } : {}),
      mediaType: "image",
      ...(intent === CREATIVE_TEMPLATE_PRODUCE_INTENT.PREVIEW
        ? { intent: CREATIVE_TEMPLATE_PRODUCE_INTENT.PREVIEW }
        : {}),
    };

    try {
      if (!templateID || !produceID || itemCount === 0) {
        return { ok: false, reason: "missingParameters" };
      }
      if (productionRowCount > PRODUCE_MAX_ROWS) {
        return { ok: false, reason: "tooManyRows" };
      }

      await dispatch(
        startSaveCreativeTemplateProduce({
          produceID,
          templateID,
          language,
          rows: persistedRows,
          columns: persistedColumns,
          itemCount,
          artboardIDs: selectedArtboardIDs,
          artboardCount: cellCount,
          variationCount: parsedVariationCount,
          variationsPerRow,
          status: CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
        })
      );

      hasActivityNotification = true;
      dispatch(
        addNotification({
          type: "producingCreativeTemplate",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
            intent,
          }),
          generationData: {
            ...payloadSnapshot,
            isGenerating: true,
          },
        })
      );

      const { jobID } = await createCreativeJob({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
        payload: {
          templateID,
          produceID,
          language,
          itemCount,
          artboardIDs: selectedArtboardIDs,
          artboardCount,
          cellCount,
          rows: serializedJobRows,
          variationCount: parsedJobVariationCount,
          variationsPerRow,
          ...(selectedSlideIDs.length > 0
            ? { slideIDs: selectedSlideIDs }
            : {}),
          ...(payloadSnapshot.intent ? { intent: payloadSnapshot.intent } : {}),
        },
      });

      dispatch(
        promoteCreativeJobNotification({ fromId: notificationId, jobID })
      );
      notificationId = jobID;
      await dispatch(
        startSaveCreativeTemplateProduce({
          produceID,
          templateID,
          language,
          rows: persistedRows,
          columns: persistedColumns,
          itemCount,
          artboardIDs: selectedArtboardIDs,
          artboardCount: cellCount,
          variationCount: parsedVariationCount,
          variationsPerRow,
          status: CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING,
          jobID,
        })
      );

      return { ok: true, jobID };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, !hasActivityNotification, [], {
          functionName: "startProduceCreativeTemplate",
          context: { templateID, produceID, itemCount, artboardCount },
          creativeJobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted?.message || error?.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      await dispatch(
        startUpdateCreativeTemplateProduceStatus({
          produceID,
          status: CREATIVE_TEMPLATE_PRODUCE_STATUS.ERROR,
          error: errorFormatted?.message || error?.message,
        })
      );

      return { ok: false, reason: "produceFailed" };
    }
  };

export const startRetryFailedProduceCreativeTemplate =
  ({
    produceID,
    templateID,
    retryFromJobID,
    failedCount = 0,
    language,
    artboardIDs = [],
    artboardCount,
  }) =>
  async (dispatch) => {
    const alertID = getUniqueID();
    let notificationId = alertID;
    const selectedArtboardIDs = Array.isArray(artboardIDs)
      ? artboardIDs.filter(Boolean)
      : [];
    const resolvedArtboardCount =
      Number(artboardCount) || Math.max(selectedArtboardIDs.length, 1);
    const retryCount = Math.max(1, Number(failedCount) || 1);
    const payloadSnapshot = {
      templateID,
      produceID,
      language,
      itemCount: retryCount,
      artboardIDs: selectedArtboardIDs,
      artboardCount: resolvedArtboardCount,
      retryFromJobID,
      mediaType: "image",
    };

    try {
      if (!templateID || !produceID || !retryFromJobID) {
        return { ok: false, reason: "missingParameters" };
      }

      dispatch(
        addNotification({
          type: "producingCreativeTemplate",
          id: alertID,
          status: ALERTS_ASYNC_STATUS.LOADING,
          progress: 0,
          jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          messageKey: getCreativeJobAlertMessageKey({
            jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          }),
          generationData: {
            ...payloadSnapshot,
            isGenerating: true,
          },
        })
      );

      const { jobID } = await createCreativeJob({
        dispatch,
        jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
        payload: {
          templateID,
          produceID,
          language,
          retryFromJobID,
          artboardIDs: selectedArtboardIDs,
          artboardCount: resolvedArtboardCount,
        },
      });

      dispatch(
        promoteCreativeJobNotification({ fromId: notificationId, jobID })
      );
      notificationId = jobID;

      await dispatch(
        startPrepareProduceRetry({
          produceID,
          jobID,
          failedCount: retryCount,
        })
      );

      return { ok: true, jobID };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, false, [], {
          functionName: "startRetryFailedProduceCreativeTemplate",
          context: { templateID, produceID, retryFromJobID, failedCount },
          creativeJobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );

      dispatch(
        updateNotification(notificationId, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: errorFormatted?.message || error?.message,
          progress: 100,
          generationData: {
            ...payloadSnapshot,
            isGenerating: false,
          },
        })
      );

      return { ok: false, reason: "produceFailed" };
    }
  };

const PREVIEW_LAYER_TIMEOUT_MS = 6 * 60 * 1000;

export const startPreviewCreativeTemplateLayer =
  ({
    templateID,
    node,
    box,
    assets,
    artboardID,
    slideID,
    sampleRow,
    platformLanguage,
  } = {}) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!templateID || !node?.id) {
        return { ok: false, reason: "missingParameters" };
      }

      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "previewCreativeTemplateLayer"
        )}/previewCreativeTemplateLayer`,
        data: {
          templateID,
          node,
          box,
          assets,
          artboardID,
          slideID,
          sampleRow,
          platformLanguage,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: PREVIEW_LAYER_TIMEOUT_MS,
      });
      if (!result?.ok) {
        return {
          ok: false,
          reason: result?.code || "previewFailed",
          message: result?.message,
        };
      }
      return { ok: true, data: result };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startPreviewCreativeTemplateLayer",
          context: { templateID, nodeID: node?.id },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "previewFailed" };
    }
  };

const GENERATE_LAYER_PROMPT_TIMEOUT_MS = 90 * 1000;

export const startGenerateCreativeTemplateLayerPrompt =
  ({ kind, originText, imageUrl, language } = {}) =>
  async (dispatch) => {
    try {
      const { role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;

      const token = await dispatch(renewToken());
      const { data: result } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl(
          "generateCreativeTemplateLayerPrompt"
        )}/generateCreativeTemplateLayerPrompt`,
        data: {
          kind,
          originText,
          imageUrl,
          language,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: GENERATE_LAYER_PROMPT_TIMEOUT_MS,
      });
      const prompt = String(result?.prompt || "").trim();
      if (!result?.ok || !prompt) {
        return {
          ok: false,
          reason: result?.code || "generatePromptFailed",
          message: result?.message,
        };
      }
      return { ok: true, prompt };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startGenerateCreativeTemplateLayerPrompt",
          context: { kind },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "generatePromptFailed" };
    }
  };

export const startListCreativeTemplatePrompts = () => async (dispatch) => {
  try {
    const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
    if (!advertiserID) {
      return { ok: false, reason: "missingAdvertiser", prompts: [] };
    }

    const snapshot = await get(ref(db, getPromptsPath({ advertiserID })));
    return {
      ok: true,
      prompts: listCreativeTemplatePrompts(snapshot.val()),
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startListCreativeTemplatePrompts",
        tags: { action: "creativeTemplates" },
        level: "error",
      })
    );
    return { ok: false, reason: "listPromptsFailed", prompts: [] };
  }
};

export const startSaveCreativeTemplatePrompt =
  ({ name, prompt } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      if (!advertiserID) return { ok: false, reason: "missingAdvertiser" };

      const nextName = String(name || "").trim();
      const nextPrompt = String(prompt || "").trim();
      if (
        !nextName ||
        nextName.length > PROMPT_LIBRARY_NAME_MAX ||
        !nextPrompt ||
        nextPrompt.length > PROMPT_LIBRARY_PROMPT_MAX
      ) {
        return { ok: false, reason: "invalidPrompt" };
      }

      const promptID = dispatch(getKeyDatabase());
      const createdAt = Date.now();
      await update(ref(db), {
        [`${getPromptsPath({ advertiserID })}/${promptID}`]: {
          name: nextName,
          prompt: nextPrompt,
          createdAt,
          createdAtNegative: createdAt * -1,
        },
      });
      return {
        ok: true,
        prompt: {
          id: promptID,
          name: nextName,
          prompt: nextPrompt,
          createdAt,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startSaveCreativeTemplatePrompt",
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "savePromptFailed" };
    }
  };

export const startDeleteCreativeTemplatePrompt =
  ({ promptID } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      const writeCheck = assertCanWrite({ role });
      if (!writeCheck.ok) return writeCheck;
      const nextID = String(promptID || "").trim();
      if (!advertiserID || !nextID) {
        return { ok: false, reason: "missingParameters" };
      }

      await update(ref(db), {
        [`${getPromptsPath({ advertiserID })}/${nextID}`]: null,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDeleteCreativeTemplatePrompt",
          context: { promptID },
          tags: { action: "creativeTemplates" },
          level: "error",
        })
      );
      return { ok: false, reason: "deletePromptFailed" };
    }
  };

export { getTemplateDocPath };
